import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from './api';
import { STORES_API_BASE } from './useStores';

/**
 * Today, hour by hour (GET /v1/stores/omni-hourly).
 *
 * Feeds the Hourly tab on the Performance page. The other tabs on that page
 * read the OMNI report, which is day-grain and refreshes overnight. This one
 * is refreshed every 15 minutes from CounterPoint and includes tickets still
 * open at the register, so a store can see the hour it is standing in. Door
 * counts are live: the api reads them straight from the counter ledger.
 *
 * The comparison is the same weekday last year (364 days back), the same basis
 * every other figure on the page uses. There is deliberately no goal or plan
 * number: the QuickSight hourly dashboard this replaces compared against a
 * sales plan that stopped being maintained in May 2026, and the 2026-08-12
 * retail review settled that the number a store should chase is last year.
 *
 * Day-so-far totals sum BOTH years over the same hours, so a part-day is never
 * compared against a full day. The cut is `data_through_hour`, the newest hour
 * we actually hold sales for, rather than the store's clock: CounterPoint
 * hands us the registers on an hourly copy, so at 2:05pm the freshest sales
 * are often from 1pm, and charging today against an extra hour of last year
 * would understate every store on every reading. `ly_full_day` carries last
 * year's finished day separately, for context.
 */

/** This year, the same weekday last year, and the comp between them. */
export type HourlyCell = {
  ty: number | null;
  ly_week: number | null;
  comp_week: number | null;
};

/** One hour's measures on one side of the comparison. */
export type HourSide = {
  sales: number | null;
  transactions: number | null;
  /** People counted through the door. Null where the store has no counter. */
  traffic: number | null;
  /** Share of visits that bought, as a decimal. Null without a door count. */
  conversion: number | null;
  /** Dollars per transaction. */
  dpt: number | null;
};

export type HourPoint = {
  /** 0 to 23 on the store's own clock. */
  hour: number;
  ty: HourSide;
  ly: HourSide;
  comp_sales: number | null;
  /** The store's local clock has passed this hour's end. */
  complete: boolean;
  /** The hour the store is in right now. */
  current: boolean;
};

export type HourlyTotals = {
  sales: HourlyCell;
  transactions: HourlyCell;
  traffic: HourlyCell;
  conversion: HourlyCell;
  dpt: HourlyCell;
};

export type PerformanceHourlyData = {
  store_no: string;
  /** The store's own local date, not the UTC date. */
  business_date: string;
  ly_date: string;
  timezone: string | null;
  /** The store's current local hour, on its own clock. */
  local_hour: number | null;
  /**
   * The newest hour this year has sales for. Every comparison is cut here, so
   * the two years are always measured over the same slice of the day.
   */
  data_through_hour: number | null;
  /** When the hourly mirror last wrote a row for this store's day. */
  updated_at: string | null;
  hours: HourPoint[];
  totals: HourlyTotals;
  ly_full_day: { sales: number | null; transactions: number | null };
  best_hour: { hour: number; sales: number } | null;
};

export function usePerformanceHourly(
  storeNo?: number,
  enabled = true,
): UseQueryResult<PerformanceHourlyData, Error> {
  const qs = storeNo !== undefined ? `?store_no=${storeNo}` : '';
  return useQuery<PerformanceHourlyData, Error>({
    queryKey: ['stores', 'omni-hourly', storeNo ?? 'own'],
    queryFn: () => apiGet<PerformanceHourlyData>(`${STORES_API_BASE}/omni-hourly${qs}`),
    // Sales come from a mirror that refreshes every 15 minutes, but door
    // counts are read live from the counter ledger, so poll every minute to
    // keep traffic current. A 60-second stale time keeps a tab switch instant.
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
    enabled,
  });
}
